import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { DeviceEventEmitter } from 'react-native';
import { SESSION_ENDED_EVENT } from '../services/auth/sessionEvents';
import aiService from '../services/aiService';
import { aiLogger } from '../utils/logger';
import { ensureAiConsent } from '../services/ai/aiConsent';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: number;
  functionCall?: {
    name: string;
    arguments: any;
    result: any;
  };
}

interface AIContextType {
  // Chat state
  messages: ChatMessage[];
  isLoading: boolean;
  isChatOpen: boolean;

  // Chat actions
  sendMessage: (message: string) => Promise<void>;
  clearMessages: () => void;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;

  // Error handling
  error: string | null;
  clearError: () => void;
}

const AIContext = createContext<AIContextType>({
  messages: [],
  isLoading: false,
  isChatOpen: false,
  sendMessage: async () => {},
  clearMessages: () => {},
  openChat: () => {},
  closeChat: () => {},
  toggleChat: () => {},
  error: null,
  clearError: () => {},
});

export const useAI = () => useContext(AIContext);

export const AIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: "Hello! I'm Orion, your MyFinalKey assistant. I'm here to help you organize your end-of-life planning in a conversational way. How can I assist you today?",
      timestamp: Date.now(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  // Bumped when the session ends; a reply that arrives for an earlier
  // session is ignored.
  const sessionGeneration = useRef(0);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(async (message: string) => {
    // Captured before the first await: if the session ends at any point
    // after this, nothing below may touch the conversation or the network.
    const generation = sessionGeneration.current;
    const stale = () => generation !== sessionGeneration.current;
    if (!(await ensureAiConsent())) return;
    if (stale()) return;
    if (!message || !message.trim()) return;

    // Add user message
    const userMessage: ChatMessage = {
      role: 'user',
      content: String(message).trim(),
      timestamp: Date.now(),
    };

    try {
      // Get current messages and build conversation history synchronously
      let currentMessages: ChatMessage[] = [];
      setMessages((prev) => {
        // Ensure prev is an array
        if (!Array.isArray(prev)) {
          aiLogger.error('Messages state corrupted, resetting');
          currentMessages = [userMessage];
          return [userMessage];
        }
        currentMessages = [...prev, userMessage];
        return currentMessages;
      });

      setIsLoading(true);
      setError(null);

      // Send to backend AI service - use currentMessages which is guaranteed to be populated
      const response = await aiService.sendMessage(currentMessages);
      if (stale()) return;

      // Validate response
      if (!response) {
        throw new Error('No response from AI service');
      }

      // Ensure we have a message (even if empty string)
      const messageContent = response.message ? String(response.message).trim() : '';

      // If no message content, use a default
      const finalContent = messageContent || 'I processed your request.';

      aiLogger.debug('AI response content length:', finalContent.length);

      // Validate and sanitize function call data
      let sanitizedFunctionCall = undefined;
      if (response.functionCall) {
        try {
          sanitizedFunctionCall = {
            name: String(response.functionCall.name || ''),
            arguments: response.functionCall.arguments || {},
            result: response.functionCall.result || {},
          };
          aiLogger.debug('Function call sanitized:', sanitizedFunctionCall.name);
        } catch (err) {
          aiLogger.error('Error sanitizing function call:', err);
          sanitizedFunctionCall = undefined;
        }
      }

      // Create AI response message
      const aiMessage: ChatMessage = {
        role: 'assistant',
        content: finalContent,
        timestamp: Date.now(),
        functionCall: sanitizedFunctionCall,
      };

      aiLogger.debug('Adding AI message to chat, content:', aiMessage.content.substring(0, 50));
      setMessages((prev) => [...prev, aiMessage]);

      // If a function was called, optionally show a system message
      if (response.functionCall) {
        const functionName = response.functionCall.name;
        const functionResult = response.functionCall.result;

        // Skip system messages for internal/context functions
        const internalFunctions = ['get_checklist_progress', 'get_user_context', 'get_successors_keyholders'];

        // Add system message about the action taken (except for internal functions)
        if (functionResult?.success && !internalFunctions.includes(functionName)) {
          const systemMessage: ChatMessage = {
            role: 'system',
            content: functionResult.message || `Action completed: ${functionName}`,
            timestamp: Date.now(),
          };
          setMessages((prev) => [...prev, systemMessage]);
        }
      }
    } catch (err: any) {
      // A failure of a request from an ended session is nobody's business now.
      if (stale()) return;
      const errorMessage = err.message || 'Failed to send message';
      setError(errorMessage);
      aiLogger.error('AI Chat Error:', err);

      // Add error message to chat so user can see what happened
      const errorChatMessage: ChatMessage = {
        role: 'system',
        content: `Error: ${errorMessage}`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorChatMessage]);
    } finally {
      // Only for its own session: a late finish must not clear the
      // loading state of a request made after the reset.
      if (!stale()) setIsLoading(false);
    }
  }, []); // Remove messages from dependency array to prevent recreation

  // The provider sits above the navigator and outlives a sign-out. When
  // the session ends, the conversation goes with it, so the next account
  // on this device starts clean (pen test W1, 2026-10-02).
  useEffect(() => {
    const subscription = DeviceEventEmitter.addListener(SESSION_ENDED_EVENT, () => {
      sessionGeneration.current += 1;
      setMessages([
        {
          role: 'assistant',
          content: "Hello! I'm Orion, your MyFinalKey assistant. How can I help you today?",
          timestamp: Date.now(),
        },
      ]);
      setIsChatOpen(false);
      setIsLoading(false);
      setError(null);
    });
    return () => subscription.remove();
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([
      {
        role: 'assistant',
        content: "Hello! I'm Orion, your MyFinalKey assistant. How can I help you today?",
        timestamp: Date.now(),
      },
    ]);
  }, []);

  // Nothing reaches OpenAI before the person agrees (aiConsent).
  const openChat = useCallback(() => {
    const generation = sessionGeneration.current;
    ensureAiConsent().then((ok) => {
      if (ok && generation === sessionGeneration.current) setIsChatOpen(true);
    });
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    // Emit event to notify checklist screens to refresh
    DeviceEventEmitter.emit('refreshChecklist');
  }, []);

  const toggleChat = useCallback(() => setIsChatOpen((prev) => !prev), []);
  const clearError = useCallback(() => setError(null), []);

  return (
    <AIContext.Provider
      value={{
        messages,
        isLoading,
        isChatOpen,
        sendMessage,
        clearMessages,
        openChat,
        closeChat,
        toggleChat,
        error,
        clearError,
      }}
    >
      {children}
    </AIContext.Provider>
  );
};
